import { Link } from "wouter";
import { Trophy, Zap, Award, Users, BookOpen, ShieldCheck, Sparkles, Target, Drama, MessageCircle } from "lucide-react";
import { APP_NAME, APP_TAGLINE } from "@shared/const";

const FEATURES = [
  { icon: Trophy, title: "Live Leaderboard", body: "Real-time rankings across 4 KPIs with sparklines, rank delta, and personal best stars." },
  { icon: Zap, title: "Gamification Engine", body: "10 levels from Solar Rookie to Solar Legend. Earn badges, build streaks, level up." },
  { icon: Award, title: "Team Competitions", body: "Sprints, marathons, head-to-head challenges with real prizes and live timers." },
  { icon: BookOpen, title: "Sales Playbook", body: "14 chapters of branded sales training. Read, mark complete, level up." },
  { icon: MessageCircle, title: "Sales Coach", body: "A senior coach in your pocket. Knows your pipeline. Gives concrete next actions." },
  { icon: Drama, title: "Practice Sandbox", body: "Roleplay objections against an AI customer. Get scored. Get better." },
  { icon: Sparkles, title: "Daily Briefings", body: "AI-personalized morning card: top priorities, mood read, contextual quote." },
  { icon: Target, title: "Vision & Goals", body: "Set targets, track progress, project commission. Visualize your path." },
  { icon: ShieldCheck, title: "Admin Control", body: "Booking criteria, lead reassignment, sync health, alerts, role gates." },
];

// Set to true once /public/sections/landing-hero.webp is dropped in.
// Until then, the gradient + blur orbs remain.
const HAS_HERO_PHOTO = false;

export default function LandingPage() {
  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center px-4 py-20">
      {/* Hero background — real photo when available, gradient otherwise */}
      {HAS_HERO_PHOTO ? (
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <img
            src="/sections/landing-hero.webp"
            alt=""
            aria-hidden="true"
            className="h-full w-full object-cover object-center opacity-70"
          />
          {/* Tint the photo toward the brand palette + ensure text legibility */}
          <div className="absolute inset-0 bg-gradient-to-b from-ink-900/40 via-ink-900/70 to-ink-900" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink-900/60 via-transparent to-ink-900/60" />
        </div>
      ) : (
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute -top-32 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-amber-500/20 blur-3xl" />
          <div className="absolute bottom-0 right-1/4 h-96 w-96 rounded-full bg-rose-500/10 blur-3xl" />
        </div>
      )}

      <div className="mx-auto max-w-4xl text-center">
        <span className="chip mx-auto mb-6 bg-amber-500/10 text-amber-300">The Most Advanced Solar Sales Platform</span>
        <h1 className="font-serif text-6xl font-bold tracking-tight text-white sm:text-7xl">
          Dominate Solar Sales.
          <br />
          <span className="bg-gradient-to-r from-emerald-400 via-amber-400 to-rose-400 bg-clip-text text-transparent">
            Track. Compete. Win.
          </span>
        </h1>
        <p className="mx-auto mt-6 max-w-2xl text-lg text-zinc-400">
          {APP_NAME} gives your team real-time performance data, competitions, coaching, and a training library — built for the way the team actually works.
        </p>
        <div className="mt-10 flex justify-center gap-4">
          <Link href="/dashboard" className="btn-primary">
            <Zap size={16} /> Login to Command Center
          </Link>
        </div>
      </div>

      <div className="mx-auto mt-20 grid max-w-6xl grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {FEATURES.map((f) => (
          <div key={f.title} className="card p-5 transition hover:border-amber-500/30">
            <f.icon size={20} className="text-amber-400" />
            <h3 className="mt-3 text-base font-semibold text-white">{f.title}</h3>
            <p className="mt-1.5 text-sm text-zinc-400">{f.body}</p>
          </div>
        ))}
      </div>

      <div className="mt-20 text-center text-xs text-zinc-600">
        Built for Northern PWR. Hosted on Cloudflare. Powered by Anthropic.
      </div>
    </div>
  );
}
